/**
 * Calculate the current reading streak from completion dates
 * A streak is consecutive days ending with today or the most recent read day
 */
import { Timestamp } from 'firebase/firestore';

export const calculateStreakFromDates = (
  completions: { date: Timestamp; dayNumber: number }[]
): number => {
  if (!completions.length) return 0;

  // Sort completions by date (most recent first)
  const sortedCompletions = completions.sort(
    (a, b) => b.date.toMillis() - a.date.toMillis()
  );

  // Get the most recent completion date
  const mostRecentDate = sortedCompletions[0].date.toDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day

  // If the most recent completion is not today or yesterday, streak is 0
  const daysSinceLastRead = Math.floor(
    (today.getTime() - mostRecentDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysSinceLastRead > 1) return 0;

  let streak = 0;
  let expectedDate = new Date(today);

  // Check consecutive days backwards from today (or most recent completion)
  for (const completion of sortedCompletions) {
    const completionDate = completion.date.toDate();
    completionDate.setHours(0, 0, 0, 0); // Normalize to start of day

    // If this completion matches the expected date in the streak
    if (completionDate.getTime() === expectedDate.getTime()) {
      streak++;
      // Move to the previous day
      expectedDate.setDate(expectedDate.getDate() - 1);
    } else if (completionDate.getTime() < expectedDate.getTime()) {
      // If we find a gap, streak is broken
      break;
    }
    // If completionDate > expectedDate, skip it (future completion, shouldn't happen)
  }

  return streak;
};

/**
 * Calculate the current reading streak from a list of completed day numbers
 * A streak is consecutive days ending with today or the most recent read day
 * @deprecated Use calculateStreakFromDates instead for more accurate streak calculation
 */
export const calculateStreak = (
  completedDays: number[],
  currentDay: number
): number => {
  if (!completedDays.length) return 0;

  // Since the array is already sorted (maintained by the service), we can work backwards from currentDay
  let streak = 0;

  for (let day = currentDay; day >= 1; day--) {
    if (completedDays.includes(day)) {
      streak++;
    } else {
      break; // Streak is broken
    }
  }

  return streak;
};

/**
 * Calculate the total progress from completed day numbers
 */
export const calculateProgress = (
  completedDays: number[],
  totalDays: number
): number => {
  return completedDays.length;
};

/**
 * Get milestone messages based on streak and progress for the current year
 */
export const getMilestoneMessage = (
  streak: number,
  progress: number,
  totalDays: number
): string | null => {
  const progressPercentage = (progress / totalDays) * 100;

  if (streak >= 30) return '🎉 Amazing! A 30-day streak is incredible!';
  if (streak >= 21) return '🌟 Fantastic! 21 days of consistent reading!';
  if (streak >= 14) return '⭐ Impressive! Two weeks of daily reading!';
  if (streak >= 7) return "🏆 Week Warrior! You've maintained a 7+ day streak!";
  if (progressPercentage >= 100)
    return "🎊 Congratulations! You've completed the entire 2026 reading plan!";
  if (progressPercentage >= 75)
    return "🚀 Three-quarters done! You're almost there!";
  if (progressPercentage >= 50)
    return "💪 Halfway Hero! You're over halfway through 2026!";
  if (progressPercentage >= 25)
    return '🎯 Quarter complete! Great progress on 2026!';

  return null;
};
